import {Component, DOCUMENT, Inject, inject, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {GroupSeriesInfoDto} from '../../dtos/groupseriesinfo.dto';
import {TextButton} from '../../../../shared/components/text-button/text-button.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {MatIconModule} from '@angular/material/icon';
import {MemoryService} from '../../../../core/services/memory.service';
import {MatDivider} from '@angular/material/divider';
import {GroupSeriesParameterDto} from '../../dtos/groupseriesparameter.dto';
import {ActivatedRoute, Router} from '@angular/router';
import {CourseService} from '../../course.service';
import {LogoComponent} from '../../../../shared/components/logo/logo.component';
import {Location} from '@angular/common';
import {LocationQueryDto} from '../../../startup/dtos/location-query.dto';
import {LocationService} from '../../../../core/services/location.service';
import {GrouAppointmentModel} from '../../models/groupappointmentmodel';
import {DateTime} from 'luxon';
import {GroupAppointmentInfoDto} from '../../dtos/groupappointmentinfo.dto';
import {MatCheckbox} from '@angular/material/checkbox';
import {LocationDetailDto} from '../../../startup/dtos/location-detail.dto';
import {BreakpointObserver} from '@angular/cdk/layout';
import {FormsModule} from "@angular/forms";
import {take} from 'rxjs';

@Component({
  selector: 'app-course-booking',
  imports: [TextButton, TranslocoPipe, MatIconModule, MatDivider, MatCheckbox, LogoComponent, TranslocoPipe, FormsModule],
  templateUrl: './course-booking.component.html',
  styleUrl: './course-booking.component.scss'
})
export class CourseBookingComponent implements OnInit {
  router: Router = inject(Router);
  location: Location = inject(Location);
  route = inject(ActivatedRoute);
  memoryService = inject(MemoryService);
  dialog: MatDialog = inject(MatDialog);
  courseService = inject(CourseService);
  locationService = inject(LocationService);
  translocoService = inject(TranslocoService);
  course!: GroupSeriesInfoDto;
  groupAppointments: GrouAppointmentModel[] = [];
  mode: 'mobile' | 'tablet' | 'desktop' = 'desktop';

  constructor(@Inject(DOCUMENT) private document: Document, private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver.observe([
      '(max-width: 980px)',     // mobile
      '(min-width: 981px) and (max-width: 999px)', // tablet
      '(min-width: 1000px)'      // desktop
    ]).subscribe(result => {
      if (result.breakpoints['(max-width: 980px)']) {
        this.mode = 'mobile';
      } else if (result.breakpoints['(min-width: 981px) and (max-width: 999px)']) {
        this.mode = 'tablet';
      } else if (result.breakpoints['(min-width: 1000px)']) {
        this.mode = 'desktop';
      }
    });
  }

  ngOnInit() {
    // Wait for the translation service to be initialized.
    this.translocoService.selectTranslation().pipe(take(1)).subscribe(() => {
    });
    const groupId = this.parseIntOrUndefined(this.route.snapshot.paramMap.get('id'));
    // We also support a direct navigation to this page. So in case the location and customer is not present, we try to load it from the url
    if (!this.memoryService.locationId) {
      const locationId = this.parseIntOrUndefined(this.route.snapshot.queryParams['locationId']);
      if (locationId) {
        this.memoryService.locationId = locationId;
      }
    }

    if (!this.memoryService.customerId) {
      const customerId = this.parseIntOrUndefined(this.route.snapshot.queryParams['customerId']);
      if (customerId) {
        this.memoryService.customerId = customerId;
      }
    }

    this.initializeLocation();
    const payload: GroupSeriesParameterDto = {
      locationId: this.memoryService.locationId,
      customerId: this.memoryService.customerId,
      patientId: this.memoryService.patientId,
      groupId: groupId
    };

    this.courseService.loadCourse(payload).subscribe({
      next: (dto: GroupSeriesInfoDto) => {
        this.course = dto;
        dto.appointments?.forEach(p => this.groupAppointments.push(new GrouAppointmentModel(p)))
      }
    });
  }

  initializeLocation() {
    const locationId = this.memoryService.locationId;
    if (!locationId) {
      return;
    }

    const locationQueryDto: LocationQueryDto = {customerId: this.memoryService.customerId!, locationIds: [locationId]};
    this.locationService.queryLocationDetails(locationQueryDto).subscribe({
      next: (locationDetailDtos: LocationDetailDto[]) => {
        const locationDetailDto = locationDetailDtos.find(l => l.locationId == locationId);
        if (!locationDetailDto) {
          return;
        }

        this.setProperty("--custom-primary-color", locationDetailDto.appearanceSettings.primaryColor);
        this.setProperty("--custom-secondary-color", locationDetailDto.appearanceSettings.secondaryColor);
        this.setProperty("--custom-tertiary-color", locationDetailDto.appearanceSettings.tertiaryColor);
        this.memoryService.locationDetailDto = locationDetailDto;
      }
    });
  }

  locationInfo() {
    return this.memoryService.locationDetailDto?.name + ', ' + this.memoryService.locationDetailDto?.contact?.street + ', ' + this.memoryService.locationDetailDto?.contact?.city;
  }

  getFormatedDate(input: string | undefined) {
    if (!input) {
      return "-";
    }

    var startTime = DateTime.fromISO(input!);
    const weekday = startTime.setLocale(this.translocoService.getActiveLang()).toFormat('EEE');
    const date = startTime.toFormat('dd.MM.yyyy');
    return `${weekday} ${date}`;
  }

  getFormatedTime(input: GroupAppointmentInfoDto) {
    if (!input) {
      return "-";
    }

    var startTime = DateTime.fromISO(input?.start!);
    var endTime = DateTime.fromISO(input?.end!);
    return `${startTime.toFormat('HH:mm')} - ${endTime.toFormat('HH:mm')}`;
  }

  getAppointmentInfo(input: GroupAppointmentInfoDto) {
    if (!input) {
      return "-";
    }

    return `${this.translocoService.translate('courses.with')} ${input.therapist}`;
  }

  getDescription() {
    if (this.course.descriptionRich && this.course.descriptionRich.length > 0) {
      return this.course.descriptionRich;
    }

    return this.course.description;
  }

  getBookingInfo(input: GroupAppointmentInfoDto) {
    if (!input) {
      return "-";
    }

    if (input.isBooked == true) {
      return this.translocoService.translate('courses.bookedinfo.booked');
    }

    if (input.bookedOut != true && input.remaining! <= 0) {
      return "-";
    }

    const key = 'courses.bookedinfo.' + (input.bookedOut == true ? 'zero' : input.remaining === 1 ? 'one' : 'other');
    return this.translocoService.translate(key, {count: input.remaining});
  }

  toggleSelection(dto: GrouAppointmentModel) {
    if (dto.dto?.bookedOut) {
      return;
    }

    dto.isChecked = !dto.isChecked;
  }

  showBooking() {
    let selected = this.groupAppointments.filter(p => p.isChecked);
    if (selected.length > 0) {
      this.router.navigate(['/course-confirm'], {state: {course: this.course, selectedAppointments: selected}});
    }
  }

  back() {
    this.location.back();
  }

  private parseIntOrUndefined(value: string | null): number {
    if (!value) {
      return 0;
    }

    const result = parseInt(value);
    return isNaN(result) ? 0 : result;
  }

  private setProperty(propertyName: string, value?: string) {
    if (!value) {
      return;
    }

    this.document.documentElement.style.setProperty(propertyName, value)
  }
}
